// Statements & Reconciliation — the per-bank-account view of every imported statement with its
// reconciliation numbers (owner-approved design, 2026-09-18). Reached from the cashbook screen's
// "Statements" button (next to "Fetch mail"). Columns, in the owner's order:
//   Ref (editable BS tag) | Statement period | Opening | Money in | Money out | Closing |
//   Ledger (in / out) | Reconciles
// A statement Ties (green) when opening + money_in − money_out == closing (±0.01), else amber.
// A gap banner row is drawn between two statements when this one's opening ≠ the previous one's
// closing (a possible missing statement). The Ledger column shows the GENERAL-LEDGER movement of
// the account's own GL account over the SAME period, next to the import values, with its own
// "ledger ties" tick. All numbers come straight from GET
// …/bank-accounts/{id}/statements/reconciliation; the Ref inline-edit PUTs …/bank-statements/{id}/reference.
// Beside each Ref, "⬇ PDF" downloads the ORIGINAL bank file the user imported for that statement
// (GET …/bank-accounts/{acct}/statements/{id}/file — the exact stored PDF/image, never regenerated).
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { notice as notify } from '../shared/notice.js';   // aliased: `notice` is this screen's banner signal
import { tenantShell } from './tshell.js';
import { fmtMoney, formatDate, isForeignAccount, baseCurrencyCode } from './bank_helpers.js';
import { t } from '../shared/i18n.js';

export function statementsScreen(companyId, accountId){
  const rows = signal([]), accountName = signal('');
  const loading = signal(true), err = signal(''), notice = signal('');
  // Bad-data shortlist (owner 2026-09-21) — the suspect lines with one-click "fix pills".
  const badData = signal([]), busyId = signal(null),
        editId = signal(null), editDate = signal(''), confirmRemove = signal(null);

  // The account's own card (name + currency). A statement's figures are in the ACCOUNT's currency;
  // the general-ledger figures beside them are in the company's base currency (owner 2026-10-19: no
  // currency bleeds into another). Read once, before any figure renders, so no statement figure
  // ever shows another currency's mark — not even for a moment.
  const cardP = api.get('/companies/' + companyId + '/bank-accounts')
    .then(a => (a.data || []).find(x => String(x.id) === String(accountId)) || null)
    .catch(() => null);
  cardP.then(found => { if(found) accountName.value = found.name || ''; });   // header name is cosmetic
  let stmtSym = '', foreign = false;                   // set together with rows / badData
  const takeCard = card => {
    const c = card || {};
    stmtSym = c.own_currency_symbol || c.currency_symbol || c.own_currency_code || c.currency_code || '';
    foreign = isForeignAccount(c, { code: baseCurrencyCode() });
  };

  function toast(message){
    notice.value = message;
    setTimeout(() => { if(notice.value === message) notice.value = ''; }, 4000);
  }

  async function load(){
    try {
      const [r, card] = await Promise.all([
        api.get('/companies/' + companyId + '/bank-accounts/' + accountId + '/statements/reconciliation'), cardP]);
      takeCard(card);
      rows.value = r.data || [];
      err.value = '';
    } catch(e){
      err.value = (e.data && e.data.error) || e.message || t('statements.err_load', 'Could not load statements');
    }
    loading.value = false;
  }
  async function loadBad(){
    try {
      const [b, card] = await Promise.all([
        api.get('/companies/' + companyId + '/bank-accounts/' + accountId + '/bad-data'), cardP]);
      takeCard(card);
      badData.value = b.data || [];
    } catch { badData.value = []; }
  }
  load();
  loadBad();

  // Apply a fix pill. `edit` opens an inline date editor (edit-in-place); the rest POST to the fix
  // endpoint, which transparently unposts→fixes→reposts a posted line, then we refresh everything.
  async function applyFix(item, pill){
    if(pill.action === 'edit'){
      editId.value = item.line_id;
      editDate.value = item.suggested_date || item.txn_date || '';
      return;
    }
    busyId.value = item.line_id;
    try {
      const body = { action: pill.action };
      if(pill.txn_date) body.txn_date = pill.txn_date;
      const r = await api.post('/companies/' + companyId + '/bank-lines/' + item.line_id + '/fix', body);
      toast(r.message || t('statements.fixed', 'Fixed'));
      confirmRemove.value = null;
      await load(); await loadBad();
    } catch(e){
      toast((e.data && e.data.error) || e.message || t('statements.fix_err', 'Could not apply the fix'));
    }
    busyId.value = null;
  }
  async function saveEditDate(item){
    const d = (editDate.value || '').trim();
    if(!d) return;
    busyId.value = item.line_id;
    try {
      const r = await api.post('/companies/' + companyId + '/bank-lines/' + item.line_id + '/fix',
        { action: 'set_date', txn_date: d });
      toast(r.message || t('statements.date_fixed', 'Date corrected'));
      editId.value = null;
      await load(); await loadBad();
    } catch(e){
      toast((e.data && e.data.error) || e.message || t('statements.fix_err', 'Could not apply the fix'));
    }
    busyId.value = null;
  }
  // Commit an edited Ref tag (blur or Enter). A no-op edit is skipped; blank clears to the default.
  async function saveRef(row, value){
    const ref = (value || '').trim();
    if(ref === (row.reference || '').trim()) return;
    try {
      await api.put('/companies/' + companyId + '/bank-statements/' + row.id + '/reference', { reference: ref });
      toast(t('statements.ref_saved', 'Reference updated'));
      await load();
    } catch(e){
      toast((e.data && e.data.error) || e.message || t('statements.ref_err', 'Could not update the reference'), 'err');
    }
  }

  // "Download PDF" (owner 2026-09-28): the ORIGINAL file imported for this statement, byte for byte.
  // The server names it after the upload; <Ref>.pdf is only the fallback if a proxy strips that
  // header. Always shown — this screen lists imported statements — so a statement with no stored
  // original (a CSV import) answers 404, which becomes an error toast, never a silent no-op. The
  // busy state is set on the clicked button itself: a signal here would re-render the whole grid
  // (and any Ref input being edited) when the download finishes. An Excel import's original is its
  // spreadsheet (owner 2026-09-29): the API marks it file_kind 'excel', and the button says Excel.
  async function downloadOriginal(row, e){
    const btn = e && e.currentTarget;
    if(btn){ if(btn.disabled) return; btn.disabled = true; }
    try {
      await api.download('/companies/' + companyId + '/bank-accounts/' + accountId + '/statements/' + row.id + '/file',
                         (row.reference || 'statement') + (row.file_kind === 'excel' ? '.xls' : '.pdf'));
    } catch(err){
      notify(err && err.status === 404
        ? t('statements.dl_missing', 'No original file is stored for {ref}.').replace('{ref}', row.reference || '')
        : (err && err.data && err.data.error) || (err && err.message) || t('statements.dl_failed', 'Could not download the statement file'),
        'bad');
    }
    if(btn) btn.disabled = false;
  }

  // statement figures (the bank's own) carry the ACCOUNT's mark; general-ledger figures the base mark
  const money = v => (v === null || v === undefined) ? '—' : fmtMoney(v, stmtSym, true);
  const mag = v => fmtMoney(v || 0, stmtSym);   // an unsigned magnitude (money in / out)
  const ledMoney = v => (v === null || v === undefined) ? '—' : fmtMoney(v, null, true);
  const ledMag = v => fmtMoney(v || 0);

  // The warning banner between two statements when the opening doesn't continue the previous close.
  const gapRow = (row, prev) => html`
    <tr class="stmt-gap-banner">
      <td colspan="7">
        <div class="alert alert-warn py-2">
          ⚠ ${t('statements.gap',
            'Possible missing statement — {prev} closed at {closed} but {this} opens at {open}, about {gap} missing')
            .replace('{prev}', (prev && prev.reference) || '?')
            .replace('{closed}', money(prev && prev.closing_balance))
            .replace('{this}', row.reference)
            .replace('{open}', money(row.opening_balance))
            .replace('{gap}', mag(Math.abs(row.gap_amount || 0)))}
        </div>
      </td>
    </tr>`;

  const tie = ok => ok
    ? html`<span class="cb-recon cb-recon-ok" title=${t('statements.ties_hint', 'Opening + money in − money out equals the closing balance')}>✓ ${t('statements.ties', 'Ties')}</span>`
    : html`<span class="cb-recon cb-recon-off" title=${t('statements.off_hint', 'The computed closing does not match the statement closing')}>△ ${t('statements.off', 'Check')}</span>`;

  // A difference line under a statement that does NOT reconcile — names the exact amount so the
  // user can go find the offending entry (owner 2026-09-21: "add another line when there is a
  // difference"). Two independent checks, each mapped to its badge:
  //   • the statement doesn't TIE  → stated closing vs its own money in/out (computed_closing)
  //   • the LEDGER movement differs → statement net vs general-ledger net over the same period
  const diffRow = row => {
    const parts = [];
    const c = row.closing_balance, cc = row.computed_closing;
    if(!row.reconciles && c != null && cc != null){
      const d = Math.round((c - cc) * 100) / 100;
      parts.push(t('statements.diff_tie',
        '⚠ {ref} out of balance by {amt} — the statement closing {closing} doesn’t match its own money in/out (that computes to {computed}). Look for a missing, duplicated or wrong line in this statement.')
        .replace('{ref}', row.reference).replace('{amt}', mag(Math.abs(d)))
        .replace('{closing}', money(c)).replace('{computed}', money(cc)));
    }
    // A FOREIGN account's statement moves in its own currency and its ledger in the base currency:
    // their difference would subtract one currency from another, so it is not shown (each figure
    // still shows above, with its own mark).
    if(!row.ledger_reconciles && !foreign){
      const netStmt = Math.round(((row.money_in || 0) - (row.money_out || 0)) * 100) / 100;
      const netLed = Math.round(((row.ledger_in || 0) - (row.ledger_out || 0)) * 100) / 100;
      const d = Math.round((netStmt - netLed) * 100) / 100;
      parts.push(t('statements.diff_ledger',
        '⚠ Ledger differs by {amt} — this statement moved {stmt} but the general ledger moved {led} over the same period (unposted or mis-posted lines).')
        .replace('{amt}', mag(Math.abs(d))).replace('{stmt}', money(netStmt)).replace('{led}', ledMoney(netLed)));
    }
    if(!parts.length) return '';
    return html`<tr class="stmt-diff-banner"><td colspan="7">
      ${parts.map(p => html`<div class="alert alert-warn py-2">${p}</div>`)}
    </td></tr>`;
  };

  // each figure cell shows the STATEMENT (import) value on top and the general-LEDGER value below it
  const twoLine = (imp, led, cls) => html`<td class="mono text-right stmt-num stmt-2line">
    <div class=${'stmt-imp ' + (cls || '')}>${imp}</div>
    <div class="stmt-led muted" title=${t('statements.ledger_line', 'General ledger')}>${led}</div></td>`;

  // The bad-data shortlist: suspect lines with valid one-click fix pills. All signals are read at
  // the TOP (no nested ${() => …} — that would append on re-render, see the web gotchas).
  const badPanel = () => {
    const items = badData.value;
    if(!items.length) return '';
    const eId = editId.value, eDate = editDate.value, busy = busyId.value, cRem = confirmRemove.value;
    return html`<div class="stmt-bad">
      <div class="stmt-bad-hd">⚠ ${t('statements.bad_title', 'Bad data')} · ${items.length}
        <span class="stmt-bad-sub muted">${t('statements.bad_sub', 'lines that look wrong — pick a fix')}</span></div>
      <div class="stmt-bad-list">
        ${items.map(it => html`<div class="stmt-bad-row">
          <div class="stmt-bad-line">
            <span class="stmt-bad-ref">${it.reference}</span>
            <span class="stmt-bad-date mono">${formatDate(it.txn_date)}</span>
            <span class="stmt-bad-descr">${it.description || ''}</span>
            <span class=${'stmt-bad-amt mono ' + ((it.amount || 0) < 0 ? 't-out' : 't-in')}>${money(it.amount)}</span>
            ${it.posted ? html`<span class="stmt-bad-posted" title=${t('statements.posted_hint', 'Already posted — the fix unposts, corrects and re-posts it')}>${t('statements.posted', 'posted')}</span>` : ''}
          </div>
          <div class="stmt-bad-reason">${it.reason}</div>
          ${eId === it.line_id
            ? html`<div class="stmt-bad-edit">
                <input class="form-control cb-in stmt-bad-dateinput" type="date" .value=${eDate}
                  @input=${e => { editDate.value = e.target.value; }}>
                <button class="btn btn-primary btn-sm" ?disabled=${busy === it.line_id}
                  @click=${() => saveEditDate(it)}>${t('common.save_changes', 'Save changes')}</button>
                <button class="btn btn-outline btn-sm" @click=${() => { editId.value = null; }}>${t('common.cancel', 'Cancel')}</button>
              </div>`
            : html`<div class="stmt-bad-pills">
                ${it.fixes.map(pill => pill.action === 'remove'
                  ? (cRem === it.line_id
                      ? html`<button class="pill pill-danger" ?disabled=${busy === it.line_id}
                            @click=${() => applyFix(it, pill)}>${t('statements.confirm_remove', 'Confirm remove')}</button>
                          <button class="pill pill-ghost" @click=${() => { confirmRemove.value = null; }}>${t('common.cancel', 'Cancel')}</button>`
                      : html`<button class="pill pill-danger-ghost" @click=${() => { confirmRemove.value = it.line_id; }}>${pill.label}</button>`)
                  : html`<button class=${'pill' + (pill.action === 'set_date' ? ' pill-primary' : '')}
                        ?disabled=${busy === it.line_id} @click=${() => applyFix(it, pill)}>${pill.label}</button>`)}
              </div>`}
        </div>`)}
      </div>
    </div>`;
  };

  const dataRow = row => html`
    <tr class=${'stmt-row' + (row.reconciles ? '' : ' stmt-row-off')}>
      <td class="stmt-td-ref"><div class="stmt-ref-cell">
        <input class="stmt-ref-input form-control" .value=${row.reference || ''}
          title=${t('statements.ref_hint', 'The statement reference tag — edit to rename')}
          @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); e.target.blur(); } }}
          @change=${e => saveRef(row, e.target.value)}>
        <button type="button" class="btn btn-outline stmt-dl"
          title=${row.file_kind === 'excel'
            ? t('statements.dl_hint_excel', 'Download Excel — the original spreadsheet you imported')
            : t('statements.dl_hint', 'Download PDF — the original statement file you imported')}
          aria-label=${(row.file_kind === 'excel'
            ? t('statements.dl_aria_excel', 'Download Excel of {ref}')
            : t('statements.dl_aria', 'Download PDF of {ref}')).replace('{ref}', row.reference || '')}
          @click=${e => downloadOriginal(row, e)}>⬇ ${row.file_kind === 'excel'
            ? t('statements.dl_label_excel', 'Excel') : t('statements.dl_label', 'PDF')}</button>
      </div></td>
      <td class="stmt-td-period">
        <div class="stmt-date">${formatDate(row.period_from)}</div>
        <div class="stmt-date">${formatDate(row.period_to)}</div>
        <span class="stmt-txns muted">· ${row.txn_count} ${row.txn_count === 1 ? t('statements.txn', 'txn') : t('statements.txns', 'txns')}</span></td>
      ${twoLine(money(row.opening_balance), ledMoney(row.ledger_opening))}
      ${twoLine(mag(row.money_in), ledMag(row.ledger_in), 't-in')}
      ${twoLine(mag(row.money_out), ledMag(row.ledger_out), 't-out')}
      ${twoLine(money(row.closing_balance), ledMoney(row.ledger_closing))}
      <td class="stmt-td-recon">${tie(row.reconciles)}
        ${row.ledger_reconciles || foreign ? '' : html`<span class="cb-recon cb-recon-off" title=${t('statements.ledger_off_hint', 'The general ledger movement does not match this statement')}>△ ${t('statements.ledger', 'ledger')}</span>`}</td>
    </tr>`;

  const body = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    if(!rows.value.length) return html`<div class="muted-block">${t('statements.empty', 'No statements imported for this account yet.')}</div>`;
    const list = rows.value;
    return html`
      <div class="table-wrap stmt-scroll">
      <table class="stmt-table cb-table">
        <thead>
          <tr>
            <th>${t('statements.col_ref', 'Ref')}</th>
            <th>${t('statements.col_period', 'Statement period')}</th>
            <th class="text-right">${t('statements.col_opening', 'Opening')}</th>
            <th class="text-right">${t('statements.col_in', 'Money in')}</th>
            <th class="text-right">${t('statements.col_out', 'Money out')}</th>
            <th class="text-right">${t('statements.col_closing', 'Closing')}</th>
            <th>${t('statements.col_reconciles', 'Reconciles')}</th>
          </tr>
        </thead>
        <tbody>
          ${list.map((row, i) => html`${row.gap_before ? gapRow(row, list[i - 1]) : ''}${dataRow(row)}${diffRow(row)}`)}
        </tbody>
      </table>
      </div>`;
  };

  const content = html`<div class="page page-wide">
    <div class="page-back"><a class="back-link" href=${`/co/${companyId}/bank`}
      @click=${e => { e.preventDefault(); navigate(`/co/${companyId}/bank`); }}>${icon('chevron-left', {size: 14})} ${() => t('common.back_to_bank', 'Back to Bank Accounts')}</a></div>
    <div class="page-head"><div>
      <h1 class="page-title">${() => t('statements.title', 'Statements & Reconciliation')}</h1>
      <div class="page-sub">${() => accountName.value || ''}</div>
    </div></div>
    ${() => notice.value ? html`<div class="alert alert-ok py-2 mb-2">${notice}</div>` : ''}
    ${badPanel}
    ${body}
  </div>`;

  return tenantShell(companyId, 'Statements', content);
}
